import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Stack,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import BlockIcon from "@mui/icons-material/BlockOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import GavelIcon from "@mui/icons-material/GavelOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  CallContact,
  CallContactEndpoints,
  ResolvedCallPolicy,
} from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import DangerConfirmDialog from "./DangerConfirmDialog";
import FamilyContactDialog, {
  consentCapacityLabel,
} from "./FamilyContactDialog";
import TableSkeleton from "./TableSkeleton";
import { EditDrawer } from "./EditDrawer";
import { describeError, useToast } from "./toast";
import { Trans, useTranslation } from "react-i18next";
import { PermissionGate, usePermission } from "../auth/access";
import { errorText } from "../utils/apiError";
import { useFormat, type Formatters } from "../i18n/format";
import type { Translate } from "../i18n/translate";

/**
 * One resident's family contacts - "Anhöriga / videosamtal" (VC-11).
 *
 * It opens from the roster row, and straight after an admit, because that is
 * where staff already are: adding the daughter is part of admitting the mother,
 * not a second errand on a page nobody remembers to visit.
 *
 * What this list refuses to do is as important as what it shows:
 *
 *  - **No phone numbers on screen.** Rows carry the server's *mask*
 *    ("+46 70 ***** 67"), which is enough to tell two daughters apart and not
 *    enough to place a call. Pulling a real number is one click, one row per
 *    click in the access log - a list view that rendered plaintext would turn a
 *    page load into a dozen silent PHI disclosures.
 *  - **No activation without consent.** The direction checkboxes are inert on a
 *    row that has no consent record, and the row reads "Inactive · consent
 *    missing" rather than looking merely unticked.
 *  - **No pretending these are permanent.** The discharge notice is at the top,
 *    not in a tooltip: family contacts are revoked with the stay (VC-06), and
 *    staff who don't know that will build habits around an assumption we break.
 */

interface Props {
  open: boolean;
  premiseId: string;
  /** The resident (PhiRecord id) these contacts belong to. */
  personRef: string;
  /** Human label for the room, for the title. */
  roomLabel: string;
  /** Premise timezone, prefilled into quiet hours. */
  defaultTz: string;
  /** Revealing a number needs PHI access; without it the button is not offered. */
  canViewPhi: boolean;
  onClose: () => void;
  /** Fired after any mutation so the roster can refresh its per-resident count. */
  onChanged?: () => void;
}

const NO_VALUE = "-";
const MASKED = "•••";

function consentLabel(t: Translate, fmt: Formatters, c: CallContact): string {
  if (!c.consent) return t("care:familyContactsDialog.consentMissing");
  const cap = consentCapacityLabel(t, c.consent.capacity);
  const when = new Date(c.consent.grantedAt);
  const date = Number.isNaN(when.getTime())
    ? c.consent.grantedAt
    : fmt.date(when);
  return cap
    ? t("care:familyContactsDialog.consentDated", { capacity: cap, date })
    : t("care:familyContactsDialog.consentGeneric", { date });
}

export default function FamilyContactsDialog({
  open,
  premiseId,
  personRef,
  roomLabel,
  defaultTz,
  canViewPhi,
  onClose,
  onChanged,
}: Props) {
  const toast = useToast();
  const { t } = useTranslation(["care", "common"]);
  const fmt = useFormat();
  const mayEdit = usePermission("video-calls", "edit", { premiseId });
  const mayViewPhi = usePermission("patient-data", "view", { premiseId });
  const [contacts, setContacts] = useState<CallContact[] | null>(null);
  const [policy, setPolicy] = useState<ResolvedCallPolicy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyRow, setBusyRow] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CallContact | null>(null);
  const [revoking, setRevoking] = useState<CallContact | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  // Revealed endpoints, keyed by contact id. Each entry cost one audited
  // disclosure, so it is cached for the life of the dialog and dropped on close.
  const [revealed, setRevealed] = useState<
    Record<string, CallContactEndpoints>
  >({});
  const [revealing, setRevealing] = useState<string | null>(null);

  /**
   * The two links a relative can be handed (VC3-5, PORTAL-2), issued straight
   * from the row.
   *
   * They used to live only inside the edit form, one click and a scroll past the
   * consent section away - which is the same as not existing: the operator
   * standing at the admission meeting with the daughter in front of them looks
   * at THIS list. Handing over the link is the job; editing the row is not.
   *
   * The minted URL is shown ONCE, so it goes in a banner at the top of the
   * dialog rather than in a menu that closes over it.
   */
  const [linkMenu, setLinkMenu] = useState<{
    anchor: HTMLElement;
    contact: CallContact;
  } | null>(null);
  const [linkBusy, setLinkBusy] = useState<string | null>(null);
  const [issued, setIssued] = useState<{
    kind: "call" | "portal";
    contactName: string;
    url: string;
    emailed: boolean;
    rotated: boolean;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const rows = await foyerApi.listCallContacts(premiseId, {
        scope: "person",
        personRef,
      });
      setContacts(rows);
      setError(null);
    } catch (err) {
      setContacts([]);
      setError(errorText(err, t));
    }
  }, [premiseId, personRef, t]);

  useEffect(() => {
    if (!open) {
      // Nothing revealed survives the dialog closing - a decrypted number left
      // on screen behind a closed dialog is a disclosure nobody asked for twice.
      setRevealed({});
      // Same rule for a minted link: it is a bearer credential for a resident's
      // camera, so it does not sit on screen after the dialog is done with.
      setIssued(null);
      setLinkMenu(null);
      return;
    }
    setContacts(null);
    void refresh();
  }, [open, refresh]);

  useEffect(() => {
    if (!open) return;
    foyerApi
      .getCallPolicy(premiseId)
      .then(setPolicy)
      .catch(() => setPolicy(null));
  }, [open, premiseId]);

  const sorted = useMemo(() => {
    const rows = contacts ?? [];
    return [...rows].sort(
      (a, b) =>
        Number(!a.consent) - Number(!b.consent) ||
        a.priority - b.priority ||
        a.displayName.localeCompare(b.displayName, fmt.locale)
    );
  }, [contacts, fmt.locale]);

  const afterMutation = async (msg: string) => {
    toast.success(msg);
    await refresh();
    onChanged?.();
  };

  /** Flip one direction straight from the row - never on an unconsented row. */
  const toggleFlag = async (
    c: CallContact,
    field: "callableFromRoom" | "canCallRoom",
    next: boolean
  ) => {
    setBusyRow(c.id);
    setError(null);
    try {
      await foyerApi.updateCallContact(premiseId, c.id, { [field]: next });
      const name = c.displayName;
      toast.success(
        field === "callableFromRoom"
          ? next
            ? t("familyContactsDialog.canBeCalled", { name })
            : t("familyContactsDialog.cannotBeCalled", { name })
          : next
            ? t("familyContactsDialog.canCall", { name })
            : t("familyContactsDialog.cannotCall", { name })
      );
      await refresh();
      onChanged?.();
    } catch (err) {
      setError(errorText(err, t));
      toast.error(
        describeError(t, "updateContact", err, { name: c.displayName })
      );
    } finally {
      setBusyRow(null);
    }
  };

  const reveal = (c: CallContact) => {
    if (revealed[c.id] || revealing) return;
    setRevealing(c.id);
    setError(null);
    foyerApi
      .revealCallEndpoints(premiseId, c.id)
      .then((e) => setRevealed((prev) => ({ ...prev, [c.id]: e })))
      .catch((err) => setError(errorText(err, t)))
      .finally(() => setRevealing(null));
  };

  const hide = (id: string) =>
    setRevealed((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  /**
   * Mint one of the two links for a contact. Both are shown once and reported
   * honestly: `emailed` says whether staff still have to pass it on themselves,
   * and for the call link `rotated` says whether a link the family already holds
   * has just stopped working.
   */
  const issueLink = async (c: CallContact, kind: "call" | "portal") => {
    setLinkMenu(null);
    setLinkBusy(c.id);
    setError(null);
    setIssued(null);
    setCopied(false);
    try {
      if (kind === "call") {
        const res = await foyerApi.sendFamilyCallLink(premiseId, c.id);
        setIssued({
          kind,
          contactName: c.displayName,
          url: res.url,
          emailed: res.emailed,
          rotated: res.rotated,
        });
      } else {
        const res = await foyerApi.sendFamilyPortalInvite(premiseId, c.id);
        setIssued({
          kind,
          contactName: c.displayName,
          url: res.url,
          emailed: res.emailed,
          rotated: false,
        });
      }
      toast.success(
        kind === "call"
          ? t("familyContactsDialog.callLinkCreated", { name: c.displayName })
          : t("familyContactsDialog.portalInviteCreated", {
              name: c.displayName,
            })
      );
    } catch (err) {
      setError(errorText(err, t));
      toast.error(
        describeError(
          t,
          kind === "call" ? "createCallLink" : "createPortalInvite",
          err,
          { name: c.displayName }
        )
      );
    } finally {
      setLinkBusy(null);
    }
  };

  /** Best-effort copy - the URL is on screen either way, this just saves typing. */
  const copyIssued = () => {
    if (!issued) return;
    void navigator.clipboard
      ?.writeText(issued.url)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  const confirmRevoke = async () => {
    if (!revoking) return;
    setRevokeBusy(true);
    setRevokeError(null);
    try {
      await foyerApi.revokeCallContact(premiseId, revoking.id);
      const name = revoking.displayName;
      setRevoking(null);
      await afterMutation(t("familyContactsDialog.revoked", { name }));
    } catch (err) {
      setRevokeError(errorText(err, t));
      toast.error(
        describeError(t, "revokeContact", err, { name: revoking.displayName })
      );
    } finally {
      setRevokeBusy(false);
    }
  };

  return (
    <>
      {/* One resident's contact list - it manages that resident, so it slides
          in from the right. Add opens a modal on top of it; Edit stacks a
          second drawer on top. */}
      <EditDrawer
        open={open}
        onClose={onClose}
        title={t("familyContactsDialog.title")}
        subtitle={roomLabel}
        width={900}
        data-testid="family-contacts-dialog"
        actions={(dismiss) => (
          <Button onClick={() => dismiss()} data-testid="family-close">
            {t("common:buttons.close")}
          </Button>
        )}
      >
        <Stack spacing={2}>
          {/* VC-06 lifecycle, stated up front rather than discovered later. */}
          <Alert severity="info" data-testid="family-discharge-notice">
            <Trans
              t={t}
              i18nKey="familyContactsDialog.dischargeNotice"
              components={{ strong: <strong /> }}
            />
          </Alert>

          {policy && !policy.enabled && (
            <Alert severity="warning" data-testid="family-calls-disabled">
              <Trans
                t={t}
                i18nKey="familyContactsDialog.callsDisabled"
                components={{ strong: <strong /> }}
              />
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {issued && (
            <Alert
              severity={issued.rotated ? "warning" : "success"}
              onClose={() => setIssued(null)}
              sx={{ wordBreak: "break-all" }}
              data-testid="family-link-result"
              action={
                <Button
                  size="small"
                  onClick={copyIssued}
                  data-testid="family-link-copy"
                >
                  {copied
                    ? t("familyContactsDialog.copied")
                    : t("common:buttons.copy")}
                </Button>
              }
            >
              <Typography variant="body2" fontWeight="fontWeightMedium">
                {issued.kind === "call"
                  ? t("familyContactsDialog.callLinkFor", {
                      name: issued.contactName,
                    })
                  : t("familyContactsDialog.portalLinkFor", {
                      name: issued.contactName,
                    })}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                {issued.rotated && t("familyContactsDialog.rotated")}{" "}
                {issued.emailed
                  ? t("familyContactsDialog.mailed")
                  : t("familyContactsDialog.notMailed")}
              </Typography>
              <strong>{issued.url}</strong>
            </Alert>
          )}

          <Stack direction="row" justifyContent="flex-end">
            <PermissionGate
              resource="video-calls"
              level="edit"
              place={{ premiseId }}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
                data-testid="family-add"
              >
                {t("familyContactsDialog.addFamilyContact")}
              </Button>
            </PermissionGate>
          </Stack>

          {contacts === null ? (
            <TableSkeleton rows={2} />
          ) : sorted.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("familyContactsDialog.empty")}
            </Typography>
          ) : (
            <Table size="small" data-testid="family-table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t("familyContactsDialog.columns.contact")}
                  </TableCell>
                  <TableCell>
                    {t("familyContactsDialog.columns.consent")}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={t(
                        "familyContactsDialog.columns.residentCanCallHint"
                      )}
                    >
                      <span>
                        {t("familyContactsDialog.columns.residentCanCall")}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={t("familyContactsDialog.columns.mayRingRoomHint")}
                    >
                      <span>
                        {t("familyContactsDialog.columns.mayRingRoom")}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {t("familyContactsDialog.columns.phone")}
                      <Tooltip
                        title={t("familyContactsDialog.columns.phoneHint")}
                      >
                        <GavelIcon
                          fontSize="inherit"
                          color="action"
                          sx={{ fontSize: 14 }}
                        />
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((c) => {
                  const revoked = !!c.revokedAt;
                  const consented = !!c.consent;
                  const inert = revoked || !consented;
                  return (
                    <TableRow
                      key={c.id}
                      data-testid={`family-row-${c.id}`}
                      sx={inert ? { opacity: 0.65 } : undefined}
                    >
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Avatar
                            src={c.avatarAssetId ?? undefined}
                            sx={{ width: 32, height: 32 }}
                          >
                            {c.displayName.slice(0, 1).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                            >
                              <Typography
                                variant="body2"
                                fontWeight="fontWeightMedium"
                              >
                                {c.displayName}
                              </Typography>
                              {revoked && (
                                <Chip
                                  size="small"
                                  color="error"
                                  label={t("familyContactsDialog.revokedChip")}
                                />
                              )}
                            </Stack>
                            {c.relation && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {c.relation}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Chip
                            size="small"
                            variant={consented ? "outlined" : "filled"}
                            color={consented ? "success" : "warning"}
                            label={consentLabel(t, fmt, c)}
                            data-testid={`family-consent-${c.id}`}
                          />
                          {!consented && !revoked && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t("familyContactsDialog.inactive")}
                            </Typography>
                          )}
                          {consented && c.consent?.note && (
                            <Tooltip title={c.consent.note}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{ maxWidth: 200 }}
                              >
                                {c.consent.note}
                              </Typography>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={c.callableFromRoom}
                          disabled={inert || busyRow === c.id || !mayEdit}
                          onChange={(e) =>
                            void toggleFlag(
                              c,
                              "callableFromRoom",
                              e.target.checked
                            )
                          }
                          inputProps={
                            {
                              "aria-label": t(
                                "familyContactsDialog.callableAria",
                                { name: c.displayName }
                              ),
                              "data-testid": `family-callable-${c.id}`,
                            } as React.InputHTMLAttributes<HTMLInputElement>
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={c.canCallRoom}
                          disabled={inert || busyRow === c.id || !mayEdit}
                          onChange={(e) =>
                            void toggleFlag(c, "canCallRoom", e.target.checked)
                          }
                          inputProps={
                            {
                              "aria-label": t(
                                "familyContactsDialog.mayRingAria",
                                { name: c.displayName }
                              ),
                              "data-testid": `family-can-ring-${c.id}`,
                            } as React.InputHTMLAttributes<HTMLInputElement>
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {revealed[c.id] ? (
                          <Stack spacing={0.25}>
                            <Typography variant="body2">
                              {revealed[c.id].phoneE164 ?? NO_VALUE}
                            </Typography>
                            <Button
                              size="small"
                              variant="text"
                              sx={{
                                alignSelf: "flex-start",
                                minWidth: 0,
                                px: 0,
                              }}
                              onClick={() => hide(c.id)}
                            >
                              {t("familyContactsDialog.hide")}
                            </Button>
                          </Stack>
                        ) : (
                          <Stack spacing={0.25}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {c.endpointHints?.phone ??
                                (c.endpointKinds?.includes("phone")
                                  ? MASKED
                                  : NO_VALUE)}
                            </Typography>
                            {canViewPhi &&
                              mayViewPhi &&
                              c.endpointKinds?.includes("phone") && (
                                <Tooltip
                                  title={t("familyContactsDialog.revealHint")}
                                >
                                  <span>
                                    <Button
                                      size="small"
                                      variant="text"
                                      startIcon={
                                        revealing === c.id ? (
                                          <CircularProgress size={12} />
                                        ) : (
                                          <VisibilityIcon />
                                        )
                                      }
                                      onClick={() => reveal(c)}
                                      disabled={revealing === c.id}
                                      sx={{
                                        alignSelf: "flex-start",
                                        minWidth: 0,
                                        px: 0,
                                      }}
                                      data-testid={`family-reveal-${c.id}`}
                                    >
                                      {t("familyContactsDialog.showNumber")}
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          <PermissionGate
                            resource="video-calls"
                            level="edit"
                            place={{ premiseId }}
                          >
                            <Tooltip
                              title={t("familyContactsDialog.linksHint")}
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={
                                    linkBusy === c.id ? (
                                      <CircularProgress size={12} />
                                    ) : (
                                      <LinkIcon />
                                    )
                                  }
                                  onClick={(e) =>
                                    setLinkMenu({
                                      anchor: e.currentTarget,
                                      contact: c,
                                    })
                                  }
                                  disabled={revoked || linkBusy === c.id}
                                  data-testid={`family-links-${c.id}`}
                                >
                                  {t("familyContactsDialog.links")}
                                </Button>
                              </span>
                            </Tooltip>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => {
                                setEditing(c);
                                setFormOpen(true);
                              }}
                              disabled={revoked}
                              data-testid={`family-edit-${c.id}`}
                            >
                              {t("common:buttons.edit")}
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<BlockIcon />}
                              onClick={() => {
                                setRevokeError(null);
                                setRevoking(c);
                              }}
                              disabled={revoked}
                              data-testid={`family-revoke-${c.id}`}
                            >
                              {t("common:buttons.revoke")}
                            </Button>
                          </PermissionGate>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          <Typography variant="caption" color="text.secondary">
            {t("familyContactsDialog.footerLog")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <Trans
              t={t}
              i18nKey="familyContactsDialog.footerLinks"
              components={{ strong: <strong /> }}
            />
          </Typography>
        </Stack>
      </EditDrawer>

      <Menu
        open={!!linkMenu}
        anchorEl={linkMenu?.anchor ?? null}
        onClose={() => setLinkMenu(null)}
      >
        <MenuItem
          onClick={() => linkMenu && void issueLink(linkMenu.contact, "call")}
          data-testid="family-issue-call-link"
        >
          {t("familyContactsDialog.sendCallLink")}
        </MenuItem>
        <MenuItem
          onClick={() => linkMenu && void issueLink(linkMenu.contact, "portal")}
          data-testid="family-issue-portal-link"
        >
          {t("familyContactsDialog.sendPortalLink")}
        </MenuItem>
      </Menu>

      <FamilyContactDialog
        open={formOpen}
        premiseId={premiseId}
        personRef={personRef}
        roomLabel={roomLabel}
        contact={editing}
        defaultTz={defaultTz}
        onClose={() => setFormOpen(false)}
        onSaved={(msg) => void afterMutation(msg)}
      />

      <DangerConfirmDialog
        open={!!revoking}
        title={t("familyContactsDialog.revokeDialog.title")}
        entityName={revoking?.displayName ?? ""}
        confirmLabel={t("common:buttons.revoke")}
        acknowledgeLabel={t("familyContactsDialog.revokeDialog.acknowledge")}
        body={
          <Typography variant="body2">
            {t("familyContactsDialog.revokeDialog.body")}
          </Typography>
        }
        errorMessage={revokeError}
        busy={revokeBusy}
        onConfirm={() => void confirmRevoke()}
        onCancel={() => setRevoking(null)}
      />
    </>
  );
}
